import { getLocalStorage } from '@/lib/core';
import { UploadService, BackupItem, BatchUploadResult } from '@/services/UploadService';
import { buildFeedbackData } from '@/utils/utils';
import { sendConversationFeedback } from '@/app/api';
import type { Msg } from '@/store/slices/room';

import logger from '@/utils/logger';

/**
 * 反馈数据管理器 - 统一管理反馈数据的备份和上传
 */
export class FeedbackDataManager {
  private storage = getLocalStorage('feedback-data');

  private storageKey = 'pending_feedback_uploads';

  private uploadService: UploadService;

  constructor(uploadServiceInstance?: UploadService) {
    this.uploadService = uploadServiceInstance || new UploadService();
  }

  /**
   * 备份会话数据到localStorage
   */
  async backup(
    msgHistory: Msg[],
    botName: string,
    sceneInfo: { name: string; roomId: string; taskId: string },
    token: string,
    sceneId: string
  ): Promise<void> {
    try {
      // 构建反馈数据
      const feedbackData = buildFeedbackData(msgHistory, botName, sceneInfo);

      // 创建备份项
      const backupItem: BackupItem = {
        id: `feedback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        data: feedbackData,
        token,
        timestamp: Date.now(),
        sceneId: sceneId || '',
      };

      // 添加到存储
      const existing = (await this.storage.get(this.storageKey)) || [];
      existing.push(backupItem);
      await this.storage.set(this.storageKey, existing);

      logger.info('[FeedbackDataManager] 会话数据已成功备份到 localStorage:', {
        id: backupItem.id,
        messageCount: msgHistory.length,
        sceneId,
        conversationCount: feedbackData.conversations?.length || 0,
      });
    } catch (error) {
      logger.error('[FeedbackDataManager] 备份会话数据失败:', error);
    }
  }

  /**
   * 上传存储的所有反馈数据
   */
  async uploadStoredData(): Promise<BatchUploadResult> {
    try {
      logger.info('[FeedbackDataManager] 检查待上传数据...');

      // 获取存储的数据
      const backupList = (await this.storage.get(this.storageKey)) || [];

      if (!backupList || !Array.isArray(backupList) || backupList.length === 0) {
        logger.info('[FeedbackDataManager] 无待上传数据');
        await this.storage.set(this.storageKey, []); // 清理可能损坏的数据
        return { successful: 0, failed: 0, results: [] };
      }

      logger.info(`[FeedbackDataManager] 发现 ${backupList.length} 条待上传反馈数据`);

      // 批量上传
      const result = await this.uploadService.batchUpload(backupList, sendConversationFeedback);

      return result;
    } catch (error) {
      logger.warn('[FeedbackDataManager] 上传存储的反馈数据失败:', error);
      return { successful: 0, failed: 0, results: [] };
    } finally {
      // 无论成功失败，都清理存储的数据
      await this.storage.set(this.storageKey, []);
      logger.info('[FeedbackDataManager] 已清理所有待上传数据');
    }
  }

  /**
   * 清理过期数据（超过指定时间的备份）
   */
  async clearExpiredData(maxAge: number = 600000): Promise<void> {
    try {
      const now = Date.now();
      const backupList = (await this.storage.get(this.storageKey)) || [];
      const filteredList = backupList.filter((item: BackupItem) => {
        return now - item.timestamp < maxAge;
      });
      await this.storage.set(this.storageKey, filteredList);
      logger.info('[FeedbackDataManager] 已清理过期数据');
    } catch (error) {
      logger.error('[FeedbackDataManager] 清理过期数据失败:', error);
    }
  }

  /**
   * 获取存储的数据数量
   */
  async getStoredCount(): Promise<number> {
    try {
      const data = await this.storage.get(this.storageKey);
      return Array.isArray(data) ? data.length : 0;
    } catch (error) {
      logger.error('[FeedbackDataManager] 获取存储数量失败:', error);
      return 0;
    }
  }

  /**
   * 获取存储的数据大小
   */
  async getStoredSize(): Promise<number> {
    try {
      const data = await this.storage.get(this.storageKey);
      return JSON.stringify(data || []).length;
    } catch (error) {
      logger.error('[FeedbackDataManager] 获取存储大小失败:', error);
      return 0;
    }
  }

  /**
   * 清空所有存储数据
   */
  async clearAll(): Promise<void> {
    await this.storage.set(this.storageKey, []);
    logger.info('[FeedbackDataManager] 已清空所有存储数据');
  }
}

// 默认反馈数据管理器实例
export const feedbackDataManager = new FeedbackDataManager();
